import { createClient } from '@supabase/supabase-js';
import { getSignupSource } from '../utils/track.js';

// Works in both the Vite browser bundle (import.meta.env) and Node
// serverless functions (process.env), where import.meta.env is undefined.
const viteEnv = import.meta.env ?? {};
const SUPABASE_URL = viteEnv.VITE_SUPABASE_URL ?? process.env.VITE_SUPABASE_URL ?? process.env.SUPABASE_URL;
const SUPABASE_KEY = viteEnv.VITE_SUPABASE_ANON_KEY ?? process.env.VITE_SUPABASE_ANON_KEY ?? process.env.SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// Founder/team/test accounts → flagged is_internal so they're excluded from
// Outreach metrics automatically (no manual chasing). Covers the @adaptos.app
// domain (founder/team) and any test/quiz/demo-prefixed address.
function isInternalEmail(email) {
  const e = (email || '').toLowerCase().trim();
  if (!e) return false;
  return /@adaptos\.app$/.test(e) || /^(test|quiz|demo|qa|staging)/.test(e.split('@')[0]);
}

// Fire-and-forget founder alert on a new signup. Skips internal/test devices
// (same flag track.js uses to suppress events). Never blocks or throws.
function notifySignup(email, source) {
  try {
    if (localStorage.getItem('adaptos_internal') === 'true') return;
    fetch('/api/notify-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, source }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* ignore */ }
}

// ─────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────
export async function signUp(email, password, name) {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  // Record where this signup came from so Outreach can attribute it (was always
  // null before, so every signup read as "Direct / unknown").
  const signup_source = getSignupSource();
  const is_internal = isInternalEmail(email);
  const { error: learnerError } = await supabase.from('learners').insert({ id: data.user.id, email, name, signup_source, is_internal });
  if (learnerError) console.error('signUp learners insert:', learnerError.message);
  const { error: streakError } = await supabase.from('streaks').insert({ learner_id: data.user.id });
  if (streakError) console.error('signUp streaks insert:', streakError.message);
  // Don't ping the founder for internal/test signups.
  if (!is_internal) notifySignup(email, signup_source);
  return data.user;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.user;
}

// ─────────────────────────────────────────────
// LEARNER IDENTITY
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// LEARNING NOTES — auto-saved takeaway artifacts from teaching turns + future
// learner-written notes. RLS-protected; learner can only read/write their own.
// ─────────────────────────────────────────────
export async function saveNote(learnerId, trackId, lessonId, content, source = 'auto') {
  if (!learnerId || !content?.trim()) return;
  const { error } = await supabase.from('learning_notes').insert({
    learner_id: learnerId,
    track_id:   trackId   || null,
    lesson_id:  lessonId  || null,
    content,
    source,
  });
  if (error) console.error('saveNote:', error.message);
}

export async function getNotes(learnerId, limit = 50) {
  if (!learnerId) return [];
  const { data, error } = await supabase
    .from('learning_notes')
    .select('id, content, source, lesson_id, track_id, created_at')
    .eq('learner_id', learnerId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) console.error('getNotes:', error.message);
  return data || [];
}

// ─────────────────────────────────────────────
// DAILY CHECK-IN (mood + feedback) — feeds M[Emory]
// Owner-only RLS; fails soft (read returns null, writes no-op) so the
// dashboard card never breaks the home view if the table is missing.
// ─────────────────────────────────────────────
export async function getLastMoodCheckin(learnerId) {
  if (!learnerId) return null;
  const { data, error } = await supabase
    .from('mood_checkins')
    .select('id, mood, score, created_at')
    .eq('learner_id', learnerId)
    .eq('kind', 'mood')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) { console.warn('getLastMoodCheckin:', error.message); return null; }
  return data || null;
}

export async function logMoodCheckin(learnerId, { mood, score = null, note = null, trackId = null } = {}) {
  if (!learnerId || !mood) return false;
  const { error } = await supabase.from('mood_checkins').insert({
    learner_id: learnerId, kind: 'mood', mood, score, note, track_id: trackId,
  });
  if (error) { console.error('logMoodCheckin:', error.message); return false; }
  return true;
}

export async function logCheckinFeedback(learnerId, { note, trackId = null } = {}) {
  if (!learnerId || !note?.trim()) return false;
  const { error } = await supabase.from('mood_checkins').insert({
    learner_id: learnerId, kind: 'feedback', note: note.trim(), track_id: trackId,
  });
  if (error) { console.error('logCheckinFeedback:', error.message); return false; }
  return true;
}

// Study-reminder preference — stored on learners.notification_preferences (JSONB).
// Delivery is a future integration; this only persists the choice.
export async function saveNotificationPreferences(learnerId, prefs) {
  if (!learnerId || !prefs) return false;
  const { error } = await supabase.from('learners')
    .update({ notification_preferences: prefs })
    .eq('id', learnerId);
  if (error) { console.error('saveNotificationPreferences:', error.message); return false; }
  return true;
}

// ─────────────────────────────────────────────
// ROADMAP ROLES — career roles the learner pinned from Career Hub.
// Stored on learners.roadmap_roles (JSONB array): { title, track_id, salary }.
// Drives the Roadmap certification-path graph + selected-roles list.
// ─────────────────────────────────────────────
export async function getRoadmapRoles(learnerId) {
  if (!learnerId) return [];
  const { data, error } = await supabase.from('learners')
    .select('roadmap_roles').eq('id', learnerId).maybeSingle();
  if (error) { console.warn('getRoadmapRoles:', error.message); return []; }
  return Array.isArray(data?.roadmap_roles) ? data.roadmap_roles : [];
}

export async function saveRoadmapRoles(learnerId, roles) {
  if (!learnerId) return false;
  const { error } = await supabase.from('learners')
    .update({ roadmap_roles: roles }).eq('id', learnerId);
  if (error) { console.error('saveRoadmapRoles:', error.message); return false; }
  return true;
}

// Admin / QA lookup — find an existing learner by email. Returns the learners
// row or null. Subject to RLS; the caller must have read permission for the row.
export async function findLearnerByEmail(email) {
  if (!email?.trim()) return null;
  const { data, error } = await supabase
    .from('learners')
    .select('*')
    .eq('email', email.trim())
    .maybeSingle();
  if (error) console.error('findLearnerByEmail:', error.message);
  return data || null;
}

export async function getLearner(userId) {
  const { data, error } = await supabase
    .from('learners').select('*').eq('id', userId).maybeSingle();
  if (error) console.error('getLearner:', error.message);
  return data;
}

// Writes the onboarding fields to the learners row and stamps
// onboarding_completed_at. Only schema-existing columns are written
// (per the approved schema-gap decision; recommended_track_source is dropped,
// experience_level was already mapped to the existing `experience` column at
// the form layer). Returns { error } for the caller to handle.
export async function completeOnboarding(userId, fields = {}) {
  const ALLOWED = new Set([
    // 7-step adaptive answers
    'experience', 'concern', 'learning_mode', 'current_track',
    'confidence_baseline', 'study_hours', 'target_date',
    // Personalization flow (employment-anchored redesign). goal_type stores the
    // intent value (repurposed signup_intent — no new column). free_lesson_limit
    // is intentionally NOT in this set so onboarding never clobbers the
    // DB-default lesson allowance.
    'age_range', 'goal_type', 'career_goal', 'active_track_id', 'cert_path',
    'overwhelm_risk_initial', 'urgency_band', 'study_capacity',
    // Account Setup (Step 0)
    'first_name', 'last_name', 'phone',
    'sms_consent', 'sms_consent_at', 'age_confirmation',
  ]);
  const safe = {};
  for (const [k, v] of Object.entries(fields)) {
    if (ALLOWED.has(k) && v !== undefined) safe[k] = v;
  }
  // Keep the existing `name` field in sync with first+last so anything that
  // reads name (the auth resolver, statePayload, the chat orb display) stays
  // consistent without having to read both halves.
  if (safe.first_name && safe.last_name) {
    safe.name = `${safe.first_name} ${safe.last_name}`;
  }
  safe.onboarding_completed_at = new Date().toISOString();
  const { error } = await supabase.from('learners').update(safe).eq('id', userId);
  if (error) console.error('completeOnboarding:', error.message);
  return { error };
}

export async function updateLearnerProfile(userId, updates) {
  const { error } = await supabase
    .from('learners').update(updates).eq('id', userId);
  if (error) console.error('updateLearnerProfile:', error.message);
  // Return the error so callers (e.g. AccountPage inline-edit / toggles) can
  // surface a real failure instead of silently showing success. Existing
  // fire-and-forget callers ignore the return.
  return { error };
}

// ─────────────────────────────────────────────
// FIRST-RUN ACTIVATION — Lesson 1 *engagement* (not "opened")
// ─────────────────────────────────────────────
// Stamp learners.lesson_1_engaged_at exactly once. Idempotent at the DB level:
// the `.is('lesson_1_engaged_at', null)` predicate means only the FIRST caller
// to win the race updates a row; every later trigger (the 30s dwell timer, a
// Continue click, a practice/checkpoint answer, completion — across tabs or
// devices) matches zero rows. That single-winner property is how we guarantee
// the lesson_1_engaged analytics event fires only once. Returns
// { firstTime } — true only when THIS call did the stamping.
//
// Standalone update (its own request) so a failure — e.g. before the
// 20260604_lesson_1_engaged migration adds the column — is swallowed/logged and
// never poisons unrelated profile writes. firstTime is false on any error, so we
// never emit a spurious event when the stamp didn't actually land.
export async function stampLesson1Engaged(userId) {
  if (!userId) return { firstTime: false };
  const { data, error } = await supabase
    .from('learners')
    .update({ lesson_1_engaged_at: new Date().toISOString() })
    .eq('id', userId)
    .is('lesson_1_engaged_at', null)
    .select('id');
  if (error) { console.error('stampLesson1Engaged:', error.message); return { firstTime: false }; }
  return { firstTime: Array.isArray(data) && data.length > 0 };
}

// First-run gate: has the learner TRULY engaged with Lesson 1? True when the
// durable stamp is set OR — as a pre-migration / pre-existing-learner fallback —
// they have any completed lesson or any checkpoint attempt on record. A bare
// "lesson opened" (current_lesson_id / an in_progress row) is intentionally NOT
// engagement, so a 3–4s open-and-leave keeps the first-run card. Each probe is
// independently fail-soft so one failing query (e.g. the stamp column missing
// pre-migration) never sinks the others. Never throws — returns false on total
// failure (the caller treats unknown as not-yet-engaged).
export async function hasEngagedLesson1(learnerId) {
  if (!learnerId) return false;
  const stamp = supabase
    .from('learners').select('lesson_1_engaged_at').eq('id', learnerId).maybeSingle()
    .then(r => !!r.data?.lesson_1_engaged_at).catch(() => false);
  const completed = supabase
    .from('lesson_progress').select('learner_id', { count: 'exact', head: true })
    .eq('learner_id', learnerId).eq('status', 'completed')
    .then(r => (r.count || 0) > 0).catch(() => false);
  const attempted = supabase
    .from('quiz_attempts').select('learner_id', { count: 'exact', head: true })
    .eq('learner_id', learnerId)
    .then(r => (r.count || 0) > 0).catch(() => false);
  const [s, c, a] = await Promise.all([stamp, completed, attempted]);
  return s || c || a;
}

export async function getLearningTrack(learnerId, trackId) {
  const { data, error } = await supabase
    .from('learning_tracks')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('track_id', trackId)
    .maybeSingle();
  if (error) console.error('getLearningTrack:', error.message);
  return data;
}

export async function upsertLearningTrack(learnerId, trackId, updates = {}) {
  const existing = await getLearningTrack(learnerId, trackId);
  if (existing) {
    const { error } = await supabase
      .from('learning_tracks').update(updates).eq('id', existing.id);
    if (error) console.error('upsertLearningTrack:', error.message);
    return existing.id;
  }
  const { data, error } = await supabase
    .from('learning_tracks')
    .insert({ learner_id: learnerId, track_id: trackId, ...updates })
    .select('id').single();
  if (error) console.error('upsertLearningTrack insert:', error.message);
  return data?.id;
}

// ─────────────────────────────────────────────
// CURRICULUM (read-only, public)
// ─────────────────────────────────────────────
export async function getTrack(trackId) {
  const { data, error } = await supabase
    .from('tracks').select('*').eq('id', trackId).maybeSingle();
  if (error) console.error('getTrack:', error.message);
  return data;
}

export async function getModules(trackId) {
  const { data, error } = await supabase
    .from('modules').select('*').eq('track_id', trackId).order('sequence_order');
  if (error) console.error('getModules:', error.message);
  return data || [];
}

export async function getLessons(moduleId) {
  const { data, error } = await supabase
    .from('lessons').select('*').eq('module_id', moduleId).order('sequence_order');
  if (error) console.error('getLessons:', error.message);
  return data || [];
}

export async function getLesson(lessonId) {
  const { data, error } = await supabase
    .from('lessons').select('*, modules(title, track_id, sequence_order, exam_domain, exam_weight)')
    .eq('id', lessonId).maybeSingle();
  if (error) console.error('getLesson:', error.message);
  return data;
}

export async function getFirstLesson(trackId) {
  // Returns the first lesson of the first module for a track
  const { data, error } = await supabase
    .from('lessons')
    .select('*, modules!inner(track_id, sequence_order)')
    .eq('modules.track_id', trackId)
    .order('sequence_order', { foreignTable: 'modules' })
    .order('sequence_order')
    .limit(1)
    .maybeSingle();
  if (error) console.error('getFirstLesson:', error.message);
  return data;
}

export async function getNextLesson(currentLessonId) {
  const current = await getLesson(currentLessonId);
  if (!current) return null;
  // Try next lesson in same module
  const { data: nextInModule, error: nextInModuleError } = await supabase
    .from('lessons')
    .select('*')
    .eq('module_id', current.module_id)
    .gt('sequence_order', current.sequence_order)
    .order('sequence_order')
    .limit(1)
    .maybeSingle();
  if (nextInModuleError) throw new Error(`getNextLesson next-in-module query failed: ${nextInModuleError.message}`);
  if (nextInModule) return nextInModule;
  // Try first lesson of next module
  const { data: nextModule, error: nextModuleError } = await supabase
    .from('modules')
    .select('*')
    .eq('track_id', current.modules.track_id)
    .gt('sequence_order', current.modules.sequence_order)
    .order('sequence_order')
    .limit(1)
    .maybeSingle();
  // Distinguish a query failure from a genuine end-of-track. A 400/500 here must
  // NOT collapse to null — that would falsely tell the learner they finished the
  // track. Throw so the caller can surface a transient error instead.
  if (nextModuleError) throw new Error(`getNextLesson next-module query failed: ${nextModuleError.message}`);
  if (!nextModule) return null;
  const { data: firstLesson, error: firstLessonError } = await supabase
    .from('lessons')
    .select('*')
    .eq('module_id', nextModule.id)
    .order('sequence_order')
    .limit(1)
    .maybeSingle();
  if (firstLessonError) throw new Error(`getNextLesson first-lesson query failed: ${firstLessonError.message}`);
  return firstLesson;
}

// ─────────────────────────────────────────────
// LEARNER STATE (injected into every API call)
// ─────────────────────────────────────────────
export async function getLearnerState(learnerId, trackId) {
  const { data, error } = await supabase
    .from('learner_state')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('track_id', trackId)
    .maybeSingle();
  if (error) console.error('getLearnerState:', error.message);
  return data;
}

// Dashboard readiness — average quiz performance scoped to a single track.
// Returns { pct, count } so the caller can distinguish "0% because failed
// every quiz" from "0% because the learner has no quiz history on this
// track yet". Per dashboard spec, the latter renders as "No data yet"
// rather than implying a real score.
export async function getTrackReadiness(learnerId, trackId) {
  if (!learnerId || !trackId) return { pct: 0, count: 0 };
  const { data, error } = await supabase
    .from('quiz_results')
    .select('score, total')
    .eq('learner_id', learnerId)
    .eq('track_id', trackId);
  if (error) { console.warn('getTrackReadiness:', error.message); return { pct: 0, count: 0 }; }
  const rows = data || [];
  if (rows.length === 0) return { pct: 0, count: 0 };
  const ratios = rows.map(r => (r.total > 0 ? r.score / r.total : 0));
  const avg = ratios.reduce((a, b) => a + b, 0) / ratios.length;
  return { pct: Math.round(avg * 100), count: rows.length };
}

export async function upsertLearnerState(learnerId, trackId, updates) {
  const { error } = await supabase
    .from('learner_state')
    .upsert({
      learner_id: learnerId,
      track_id:   trackId,
      ...updates,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'learner_id,track_id' });
  if (error) console.error('upsertLearnerState:', error.message);
}

// ─────────────────────────────────────────────
// STATE PAYLOAD BUILDER
// Assembles the full injection object for every GPT call
// ─────────────────────────────────────────────
export async function buildStatePayload(learnerId, trackId, lessonId) {
  const [state, gaps, lesson, progress, profile] = await Promise.all([
    getLearnerState(learnerId, trackId),
    getActiveKnowledgeGaps(learnerId),
    lessonId ? getLesson(lessonId) : null,
    lessonId ? getLessonProgress(learnerId, lessonId) : null,
    // Onboarding fields — forwarded for engines to consume (experienceEngine /
    // motivationEngine read what they need; safe defaults applied here).
    getLearner(learnerId),
  ]);

  return {
    learner_id:       learnerId,
    learner_profile: {
      experience:          profile?.experience          || 'beginner',
      confidence_baseline: profile?.confidence_baseline || 'medium',
      study_hours:         profile?.study_hours         || 1,
      target_date:         profile?.target_date         || null,
      learning_mode:       profile?.learning_mode       || null,
      concern:             profile?.concern             || null,
    },
    skill_domain:     trackId,
    current_track:    trackId,
    current_module:   lesson?.modules?.title || null,
    current_lesson:   lesson?.title || null,
    lesson_objectives: lesson?.objectives || [],
    // Resume signal — the RESUME RULE in promptCompiler reads this to suppress
    // re-orientation when a lesson is already underway, even if no rolling
    // summary exists yet (docs/LESSON_FLOW.md § Resume). Only the fields the
    // prompt needs are forwarded.
    lesson_progress: progress
      ? { status: progress.status, inline_checkpoints_passed: progress.inline_checkpoints_passed ?? 0 }
      : null,
    mastery_state: {
      level:          state?.mastery_state    || 'struggling',
      mastery_score:  state?.mastery_score    || 0,
      stability_score: state?.stability_score || 0,
      transfer_score: state?.transfer_score   || 0,
    },
    error_history: {
      recent_errors: (gaps || []).slice(0, 5).map(g => ({
        concept:    g.concept,
        error_type: g.error_type,
        frequency:  g.frequency,
      })),
    },
    confidence_profile: {
      avg_confidence:   state?.confidence_score  || 0,
      calibration_error: state?.calibration_error || 0,
    },
    last_checkpoint_result: state?.last_checkpoint_result || null,
    last_error_type:        state?.last_error_type        || null,
    state_trust_score:      state?.state_trust_score      || 1,
    consecutive_failures:   state?.consecutive_failures   ?? 0,
  };
}

// ─────────────────────────────────────────────
// CONCEPT NODES
// ─────────────────────────────────────────────
export async function upsertConceptNode(learnerId, lessonId, concept, status, accuracyEntry) {
  const { data: existing, error: existingError } = await supabase
    .from('concept_nodes')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('lesson_id', lessonId)
    .eq('concept', concept)
    .maybeSingle();
  if (existingError) console.error('upsertConceptNode read:', existingError.message);

  const history = existing?.accuracy_history || [];
  if (accuracyEntry !== undefined) history.push(accuracyEntry);

  const { error } = await supabase
    .from('concept_nodes')
    .upsert({
      learner_id:       learnerId,
      lesson_id:        lessonId,
      concept,
      status,
      accuracy_history: history.slice(-20), // keep last 20
      last_tested:      new Date().toISOString(),
      updated_at:       new Date().toISOString(),
    }, { onConflict: 'learner_id,lesson_id,concept' });
  if (error) console.error('upsertConceptNode:', error.message);
}

// ─────────────────────────────────────────────
// KNOWLEDGE GAPS (highest valuation value)
// ─────────────────────────────────────────────
export async function getActiveKnowledgeGaps(learnerId) {
  const { data, error } = await supabase
    .from('knowledge_gaps')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('resolved', false)
    .order('severity', { ascending: false })
    .order('frequency', { ascending: false });
  if (error) console.error('getActiveKnowledgeGaps:', error.message);
  return data || [];
}

// knowledge_gaps.error_type CHECK allows only: misconception | gap | recall |
// application. Callers send non-conforming values — handleQuizComplete sends the
// literal 'persistent_gap', and the eval path forwards the model's error_type
// (e.g. CONCEPT_GAP, MISCONCEPTION, terminology_confusion, APPLICATION_ERROR).
// Normalize to the nearest allowed value before insert (same pattern as the
// interaction_log.remediation_action map); unknown values fall back to 'gap' so
// the gap is still recorded rather than rejected by the constraint.
const ERROR_TYPE_ALLOWED = ['misconception', 'gap', 'recall', 'application'];
const ERROR_TYPE_MAP = {
  // gap family
  persistent_gap: 'gap', prerequisite_gap: 'gap', prerequisite: 'gap',
  concept_gap: 'gap', knowledge_gap: 'gap', remediate: 'gap', remediate_prerequisite: 'gap',
  // misconception family
  terminology_confusion: 'misconception', conceptual: 'misconception',
  reframe: 'misconception', confusion: 'misconception',
  // recall family
  recall_failure: 'recall', memory: 'recall', repeat: 'recall', forgot: 'recall',
  // application family
  application_error: 'application', transfer: 'application', applied: 'application', simplify: 'application',
};
function normalizeErrorType(raw) {
  const v = (raw ?? '').toString().trim().toLowerCase();
  if (ERROR_TYPE_ALLOWED.includes(v)) return v;
  return ERROR_TYPE_MAP[v] || 'gap';   // safe default — record the gap, never drop the write
}

export async function upsertKnowledgeGap(learnerId, concept, errorType, severity, lessonId) {
  const { data: existing, error: existingError } = await supabase
    .from('knowledge_gaps')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('concept', concept)
    .eq('resolved', false)
    .maybeSingle();
  if (existingError) console.error('upsertKnowledgeGap read:', existingError.message);

  if (existing) {
    const { error } = await supabase
      .from('knowledge_gaps')
      .update({
        frequency: existing.frequency + 1,
        severity,
        last_seen: new Date().toISOString(),
      })
      .eq('id', existing.id);
    if (error) console.error('upsertKnowledgeGap update:', error.message);
  } else {
    const { error } = await supabase.from('knowledge_gaps').insert({
      learner_id:              learnerId,
      concept,
      error_type:              normalizeErrorType(errorType),
      severity,
      detected_from_lesson_id: lessonId,
    });
    if (error) console.error('upsertKnowledgeGap insert:', error.message);
  }
}

export async function resolveKnowledgeGap(learnerId, concept) {
  const { error } = await supabase
    .from('knowledge_gaps')
    .update({ resolved: true, resolved_at: new Date().toISOString() })
    .eq('learner_id', learnerId)
    .eq('concept', concept);
  if (error) console.error('resolveKnowledgeGap:', error.message);
}

// ─────────────────────────────────────────────
// QUIZ ATTEMPTS
// Generic passthrough that accepts either of two shapes:
//   (1) per-question row — submitAnswer: { learner_id, lesson_id, checkpoint_type,
//       question_text, learner_answer, correct_answer, is_correct, response_time_seconds }
//   (2) per-attempt summary — handleQuizComplete: { learner_id, lesson_id,
//       attempt_number, score (0-1), failed_concepts[], remediation_strategy,
//       video_triggered, created_at }
// Both shapes write to the same quiz_attempts table; the table needs the union
// of these columns (per-question fields nullable for summary rows, attempt fields
// nullable for per-question rows).
// ─────────────────────────────────────────────
export async function logQuizAttempt(data) {
  const { error } = await supabase.from('quiz_attempts').insert(data);
  if (error) console.error('logQuizAttempt:', error.message);
}

export async function getQuizAttempts(learnerId, lessonId) {
  const { data, error } = await supabase
    .from('quiz_attempts')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('lesson_id', lessonId)
    .order('created_at', { ascending: false });
  if (error) console.error('getQuizAttempts:', error.message);
  return data || [];
}

// ─────────────────────────────────────────────
// LESSON PROGRESS
// ─────────────────────────────────────────────
export async function getLessonProgress(learnerId, lessonId) {
  const { data, error } = await supabase
    .from('lesson_progress')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('lesson_id', lessonId)
    .maybeSingle();
  if (error) console.error('getLessonProgress:', error.message);
  return data;
}

export async function upsertLessonProgress(learnerId, lessonId, trackId, moduleId, updates) {
  const { error } = await supabase
    .from('lesson_progress')
    .upsert({
      learner_id: learnerId,
      lesson_id:  lessonId,
      track_id:   trackId,
      module_id:  moduleId,
      ...updates,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'learner_id,lesson_id' });
  if (error) console.error('upsertLessonProgress:', error.message);
}

// Stamp full lesson-completion state when an end-of-lesson checkpoint passes.
// upsertLessonProgress already writes status='completed' on the quiz-pass path,
// but never the completion METADATA — completed_at / end_checkpoint_passed /
// end_checkpoint_score were left null. This records them in one upsert on the
// (learner_id, lesson_id) unique constraint. `score` is the 0-1 quiz accuracy.
// Next-lesson navigation stays in the engine (handleLessonAdvance) — this only
// persists completion.
export async function markLessonComplete(learnerId, lessonId, trackId, moduleId, score, extra = {}) {
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('lesson_progress')
    .upsert({
      learner_id:           learnerId,
      lesson_id:            lessonId,
      track_id:             trackId,
      module_id:            moduleId,
      status:               'completed',
      end_checkpoint_passed: true,
      end_checkpoint_score:  score,
      mastery_score:        score,
      completed_at:         now,
      updated_at:           now,
      ...extra,
    }, { onConflict: 'learner_id,lesson_id' });
  if (error) console.error('markLessonComplete:', error.message);
}

export async function getTrackProgress(learnerId, trackId) {
  const { data, error } = await supabase
    .from('lesson_progress')
    .select('*, lessons(title, sequence_order, module_id)')
    .eq('learner_id', learnerId)
    .eq('track_id', trackId);
  if (error) console.error('getTrackProgress:', error.message);
  return data || [];
}

// ─────────────────────────────────────────────
// LEARNING SESSIONS
// ─────────────────────────────────────────────
export async function startSession(learnerId, trackId) {
  const { data, error } = await supabase
    .from('learning_sessions')
    .insert({ learner_id: learnerId, track_id: trackId })
    .select('id').single();
  if (error) { console.error('startSession:', error.message); return null; }
  return data.id;
}

export async function endSession(sessionId, stats = {}) {
  const { error } = await supabase
    .from('learning_sessions')
    .update({
      ended_at:         new Date().toISOString(),
      ...stats,
    })
    .eq('id', sessionId);
  if (error) console.error('endSession:', error.message);
}

export async function incrementSessionStat(sessionId, field) {
  // Uses RPC for atomic increment — falls back to read+write if RPC unavailable
  const { data: session, error: readError } = await supabase
    .from('learning_sessions').select(field).eq('id', sessionId).maybeSingle();
  if (readError) console.error('incrementSessionStat read:', readError.message);
  if (!session) return;
  const { error: writeError } = await supabase
    .from('learning_sessions')
    .update({ [field]: (session[field] || 0) + 1 })
    .eq('id', sessionId);
  if (writeError) console.error('incrementSessionStat write:', writeError.message);
}

// ─────────────────────────────────────────────
// INTERACTION LOG (audit layer — every turn)
// ─────────────────────────────────────────────
export async function logInteraction(data) {
  const { error } = await supabase.from('interaction_log').insert({
    ...data,
    created_at: new Date().toISOString(),
  });
  if (error) console.error('logInteraction:', error.message);
}

// ─────────────────────────────────────────────
// MESSAGES (conversation history)
// ─────────────────────────────────────────────
export async function saveMessage(learnerId, trackId, lessonId, sessionId, role, content, phase = 'LESSON') {
  if (!learnerId || !content?.trim()) return;
  const { error } = await supabase.from('messages').insert({
    learner_id: learnerId,
    track_id:   trackId,
    domain_id:  lessonId,
    session_id: sessionId,
    role,
    content,
    phase,
  });
  if (error) console.error('saveMessage:', error.message);
}

export async function getMessages(learnerId, lessonId, limit = 20) {
  const { data, error } = await supabase
    .from('messages')
    .select('role, content')
    .eq('learner_id', learnerId)
    .eq('domain_id', lessonId)
    .order('created_at', { ascending: true })
    .limit(limit);
  if (error) console.error('getMessages:', error.message);
  return data || [];
}

// ─────────────────────────────────────────────
// TRACK READINESS
// ─────────────────────────────────────────────
export async function upsertTrackReadiness(learnerId, trackId, overallScore, domainScores) {
  const { error } = await supabase
    .from('track_readiness')
    .upsert({
      learner_id:    learnerId,
      track_id:      trackId,
      overall_score: overallScore,
      domain_scores: domainScores,
      updated_at:    new Date().toISOString(),
    }, { onConflict: 'learner_id,track_id' });
  if (error) console.error('upsertTrackReadiness:', error.message);
}

// ─────────────────────────────────────────────
// YOUTUBE INTERACTIONS
// ─────────────────────────────────────────────
export async function logYoutubeInteraction(learnerId, lessonId, concept, query) {
  const { data, error } = await supabase
    .from('youtube_interactions')
    .insert({ learner_id: learnerId, lesson_id: lessonId, concept, query })
    .select('id').single();
  if (error) { console.error('logYoutubeInteraction:', error.message); return null; }
  return data.id;
}

export async function markVideoWatched(interactionId, durationSeconds) {
  const { error } = await supabase
    .from('youtube_interactions')
    .update({ watched: true, duration_watched_seconds: durationSeconds })
    .eq('id', interactionId);
  if (error) console.error('markVideoWatched:', error.message);
}

// ─────────────────────────────────────────────
// STREAKS
// ─────────────────────────────────────────────
export async function getStreak(learnerId) {
  if (!learnerId) return null;
  const { data, error } = await supabase
    .from('streaks').select('*').eq('learner_id', learnerId).maybeSingle();
  if (error) console.error('getStreak:', error.message);
  return data || null;
}

export async function updateStreak(learnerId) {
  if (!learnerId) return;
  const today     = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const { data: existing, error: readError } = await supabase
    .from('streaks').select('*').eq('learner_id', learnerId).maybeSingle();
  if (readError) console.error('updateStreak read:', readError.message);

  if (!existing) {
    const { error } = await supabase.from('streaks').insert({
      learner_id: learnerId, current_streak: 1,
      longest_streak: 1, last_study_date: today, total_sessions: 1,
    });
    if (error) console.error('updateStreak insert:', error.message);
    return;
  }

  if (existing.last_study_date === today) return;

  let currentStreak = existing.last_study_date === yesterday
    ? existing.current_streak + 1
    : 1;

  const { error: updateError } = await supabase.from('streaks').update({
    current_streak:  currentStreak,
    longest_streak:  Math.max(currentStreak, existing.longest_streak),
    last_study_date: today,
    total_sessions:  existing.total_sessions + 1,
    updated_at:      new Date().toISOString(),
  }).eq('learner_id', learnerId);
  if (updateError) console.error('updateStreak update:', updateError.message);
}

// ─────────────────────────────────────────────
// SESSION BEHAVIOR STATE
// Captures the "how to be with this learner" context.
// Updated after every meaningful interaction.
// Injected as behavioral system message on every API call.
// Never exposed directly to the learner.
// ─────────────────────────────────────────────

const DEFAULT_BEHAVIOR_STATE = {
  current_interaction_mode:  'scaffolded_lesson',
  learner_confidence:        'unknown',
  learner_friction:          'low',
  preferred_pace:            'slower',
  preferred_density:         'low_medium',
  last_emotional_signal:     null,
  last_success:              null,
  last_error_pattern:        null,
  current_support_need:      'guided',
  tone_guidance:             'calm, reassuring, concrete, not corrective',
  momentum_bridge:           null,
  next_best_response_style:  'orient → model → one checkpoint',
};

// Decay thresholds per emotional state (minutes before resetting to neutral)
const EMOTIONAL_DECAY_MINUTES = {
  PANIC:             15,
  SHAME:             30,  // stickier — deeper psychological impact
  COGNITIVE_OVERLOAD: 10,
  FATIGUE:           20,
  CONFUSION:         10,
  AVOIDANCE:         20,
  MOTIVATION_DIP:    25,
  OVERCONFIDENCE:    15,
  CONFIDENCE:        10,
};

// Fields reset when emotional state decays
const EMOTIONAL_DECAY_RESET = {
  last_emotional_signal:    null,
  learner_confidence:       'unknown',
  learner_friction:         'low',
  current_support_need:     'guided',
  tone_guidance:            'calm, reassuring, concrete, not corrective',
  next_best_response_style: 'orient → model → one checkpoint',
  momentum_bridge:          null,
};

export async function getSessionBehaviorState(learnerId, sessionId) {
  if (!learnerId) return DEFAULT_BEHAVIOR_STATE;
  const query = supabase
    .from('session_behavior_state')
    .select('*')
    .eq('learner_id', learnerId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (sessionId) query.eq('session_id', sessionId);
  const { data, error } = await query;
  if (error || !data?.length) return DEFAULT_BEHAVIOR_STATE;

  const state = data[0];

  // ── Emotional decay check ───────────────────────────────────
  // If emotional signal is stale, reset to neutral before returning.
  // Humans don't sustain acute emotional states indefinitely.
  const signal = state.last_emotional_signal;
  const decayMinutes = signal ? EMOTIONAL_DECAY_MINUTES[signal] : null;
  if (decayMinutes && state.updated_at) {
    const ageMinutes = (Date.now() - new Date(state.updated_at).getTime()) / 60000;
    if (ageMinutes >= decayMinutes) {
      const decayed = { ...state, ...EMOTIONAL_DECAY_RESET };
      // Write decay back to Supabase async — don't block state return
      supabase.from('session_behavior_state')
        .update({ ...EMOTIONAL_DECAY_RESET, updated_at: new Date().toISOString() })
        .eq('id', state.id)
        .then(({ error: e }) => { if (e) console.warn('Emotional decay write failed:', e.message); });
      return decayed;
    }
  }

  return state;
}

export async function upsertSessionBehaviorState(learnerId, sessionId, updates) {
  if (!learnerId) return;
  // True upsert on the (learner_id, session_id) unique constraint. Only the
  // provided fields are written, so concurrent partial writes (e.g. the
  // beat_history write-back in api/chat.js) merge into the same row without
  // clobbering each other's columns.
  const { error } = await supabase
    .from('session_behavior_state')
    .upsert({
      learner_id: learnerId,
      session_id: sessionId,
      ...updates,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'learner_id,session_id' });
  if (error) console.error('upsertSessionBehaviorState:', error.message);
}

// ─────────────────────────────────────────────
// EMOTIONAL STATE DETECTION PATTERNS
// Priority-ordered — first match wins for the primary emotional signal.
// Each entry maps a detection regex to a full orchestration response.
// The model receives these as behavioral state — it does not detect them itself.
// ─────────────────────────────────────────────
const EMOTIONAL_PATTERNS = [
  {
    // Exam timeline crisis — highest urgency
    state:   'PANIC',
    pattern: /running out of time|exam (is )?(tomorrow|today|next week|in \d)|test (is )?(tomorrow|today|next week)|not ready|freaking out|panic|haven't studied|no time left/i,
    updates: {
      last_emotional_signal:    'PANIC',
      learner_confidence:       'crisis',
      learner_friction:         'exam timeline pressure',
      current_support_need:     'stabilize first — compress to highest-yield concepts only, quick wins, no scope expansion',
      tone_guidance:            'calm, focused, no pressure amplification — be the steady presence',
      next_best_response_style: 'stabilize → highest-yield concept → one checkpoint → next step',
      preferred_pace:           'exam_accelerated',
      preferred_density:        'high_essential_only',
      momentum_bridge:          "We have what we need. Let's focus on the concepts that show up most on the exam.",
    },
  },
  {
    // Self-judgment is blocking retention — psychological safety at risk
    state:   'SHAME',
    pattern: /i('m| am) (stupid|dumb|bad at this|terrible at this)|should(n't)? (already )?know this|everyone (else|probably) (knows|gets)|can't do this|why don't i (get|understand)|i feel (dumb|stupid|like an idiot)|what('s| is) wrong with me/i,
    updates: {
      last_emotional_signal:    'SHAME',
      learner_confidence:       'low',
      learner_friction:         'self-judgment blocking retention',
      current_support_need:     'normalize strongly before any content — remove shame, avoid hollow praise, rebuild safety',
      tone_guidance:            'warm, normalizing, never corrective — do not say "great job"',
      next_best_response_style: 'normalize the gap → reframe difficulty as normal → smallest possible win first',
      preferred_pace:           'slow_concrete',
      momentum_bridge:          "This concept trips up most people at this stage. That's not a reflection of your ability.",
    },
  },
  {
    // Working memory at capacity — different from general confusion
    state:   'COGNITIVE_OVERLOAD',
    pattern: /too much (information|info|to (remember|absorb|take in))|my head (is )?(spinning|full|swimming)|can't keep track|everything (is )?(blurring|blending)|overwhelmed by (all|the amount|how much)|can't process (all|this|it)/i,
    updates: {
      last_emotional_signal:    'COGNITIVE_OVERLOAD',
      learner_confidence:       'fragile',
      learner_friction:         'working memory at capacity',
      current_support_need:     'strip to one concept only — no scenarios, no framework yet, single compressed model and stop',
      tone_guidance:            'minimal, slow, spacious — reduce density immediately',
      next_best_response_style: 'one compressed model → confirm understanding → nothing else this turn',
      preferred_pace:           'slow_concrete',
      preferred_density:        'minimal',
    },
  },
  {
    // Cognitive capacity depleted — needs relief not more content
    state:   'FATIGUE',
    pattern: /tired|exhausted|brain (is )?(fried|done|dead|toast)|can't focus|losing focus|zoning out|need a break|been at this (for|a while)|too long/i,
    updates: {
      last_emotional_signal:    'FATIGUE',
      learner_confidence:       'draining',
      learner_friction:         'cognitive fatigue — capacity depleted',
      current_support_need:     'shorten the exchange, land one small win, suggest a natural stopping point',
      tone_guidance:            'light, warm — give permission to rest without guilt',
      next_best_response_style: 'one small win → session wrap → clear next step for return',
      preferred_pace:           'slow_concrete',
      preferred_density:        'low_medium',
      momentum_bridge:          "Let's land one more concept cleanly and wrap here. You've made real progress today.",
    },
  },
  {
    // Relevance not established — learner is checking out
    state:   'AVOIDANCE',
    pattern: /skip (this|it)|do i (really )?need (to know )?this|is this (on|going to be on) the exam|does this (even )?matter|can we (just )?move on|not (that )?important|why (do|are) (we|I) (learning|doing|covering) this/i,
    updates: {
      last_emotional_signal:    'AVOIDANCE',
      learner_confidence:       'disengaged',
      learner_friction:         'relevance not established',
      current_support_need:     'lead with L0 context — connect to exam impact and real job use before any content',
      tone_guidance:            'direct, honest about exam weight, never preachy',
      next_best_response_style: 'relevance hook → compressed model → one exam-pattern question',
      preferred_pace:           'faster',
    },
  },
  {
    // Motivational disconnection — losing the "why"
    state:   'MOTIVATION_DIP',
    pattern: /this is boring|not sure (about )?this|maybe this isn't for me|losing (motivation|interest|steam)|not feeling (it|this)|why (am i (doing|learning)|bother(ing)?)|is this worth it|don't (really )?care/i,
    updates: {
      last_emotional_signal:    'MOTIVATION_DIP',
      learner_confidence:       'low',
      learner_friction:         'motivational disconnect — lost the why',
      current_support_need:     'anchor to original goal first — quick tangible win before returning to content',
      tone_guidance:            'honest, grounding, connect to their stated goal — no cheerleading',
      next_best_response_style: 'goal anchor → quick win → forward motion',
      momentum_bridge:          "Let's connect this back to why you started.",
    },
  },
  {
    // Overestimating readiness — needs calibration not validation
    state:   'OVERCONFIDENCE',
    pattern: /i (already )?know (this|it)|i got (this|it)|this is (easy|simple|obvious|basic)|don't need (this|to review|to go over)|already (know|understand|get) (this|it|all of this)/i,
    updates: {
      last_emotional_signal:    'OVERCONFIDENCE',
      learner_confidence:       'high_uncalibrated',
      learner_friction:         'overestimating readiness',
      current_support_need:     'calibrate gently — introduce edge case or exam trap before accepting readiness',
      tone_guidance:            'respectful, curious, not condescending — treat it as a test worth taking',
      next_best_response_style: 'acknowledge confidence → edge case or exam trap → calibration checkpoint',
    },
  },
  {
    // Standard confusion — last in priority
    state:   'CONFUSION',
    pattern: /confused|don't understand|not sure|lost|stuck|what does (that|this) mean|i don't get (it|this)|help( me)?/i,
    updates: {
      last_emotional_signal:    'CONFUSION',
      learner_confidence:       'low',
      learner_friction:         'current explanation not landing',
      current_support_need:     'normalize confusion, simplify model, reassure',
      tone_guidance:            'calm, reassuring, concrete, not corrective',
      next_best_response_style: 'normalize → simpler model → tiny checkpoint',
      preferred_pace:           'slow_concrete',
    },
  },
];

// ─────────────────────────────────────────────
// INFER BEHAVIOR UPDATES
// Detects emotional state and contextual signals from learner input.
// Returns a partial update object for session_behavior_state.
// Called after every user message and after every eval result.
// The model receives these as behavioral state — it does not detect them itself.
// ─────────────────────────────────────────────
export function inferBehaviorUpdates(userInput, evalResult, sessionPhase) {
  const updates = {};
  const input   = userInput?.toLowerCase() || '';

  // ── Emotional state detection (priority-ordered, first match wins) ──────
  if (input) {
    for (const { updates: emotionUpdates, pattern } of EMOTIONAL_PATTERNS) {
      if (pattern.test(input)) {
        Object.assign(updates, emotionUpdates);
        break;
      }
    }
  }

  // ── Pace/density signals (stack with emotional state, don't override it) ─
  if (/faster|speed up|too slow/i.test(input) && !updates.last_emotional_signal) {
    updates.preferred_density        = 'low';
    updates.preferred_pace           = 'faster';
    updates.learner_friction         = 'pace preference';
    updates.current_support_need     = 'guided compression — shorter with validation';
    updates.next_best_response_style = 'compressed models → one checkpoint';
  }

  // ── Confidence signal (stack only if no emotional state already detected) ─
  if (/got it|i see|makes sense|understand|clear now/i.test(input) && !updates.last_emotional_signal) {
    updates.last_emotional_signal = 'CONFIDENCE';
    updates.learner_confidence    = 'improving';
  }

  // ── Eval result integration (||= guards prevent overwriting emotional state) ─
  if (evalResult?.correct === true) {
    updates.learner_confidence       = 'improving';
    updates.last_success             = evalResult.targetConcept || 'checkpoint passed';
    updates.last_error_pattern       = null;
    updates.current_support_need   ||= 'confirm success, increase difficulty slightly';
    updates.next_best_response_style ||= 'specific praise → harder scenario';
  } else if (evalResult?.correct === false) {
    updates.learner_confidence     ||= 'fragile';
    updates.last_error_pattern       = evalResult.reasoningPattern || evalResult.errorType;
    updates.current_support_need   ||= 'micro-correction + simpler retry';
    updates.tone_guidance          ||= 'calm, not grading, reduce pressure';
    updates.next_best_response_style ||= 'identify gap → simplify → retry';
  }

  // ── Phase-based interaction mode ─────────────────────────────────────────
  if (sessionPhase === 'REMEDIATION') {
    updates.current_interaction_mode = 'remediation';
    updates.current_support_need   ||= 'repair and retry';
  } else if (sessionPhase === 'LESSON') {
    // Mid-lesson with good confidence and no strong emotional signal → exploring.
    const strongEmotion = ['PANIC', 'SHAME', 'COGNITIVE_OVERLOAD', 'FATIGUE', 'AVOIDANCE', 'MOTIVATION_DIP', 'OVERCONFIDENCE'].includes(updates.last_emotional_signal);
    const goodConfidence = updates.learner_confidence === 'improving' || updates.last_emotional_signal === 'CONFIDENCE';
    updates.current_interaction_mode = (!strongEmotion && goodConfidence) ? 'exploring' : 'lesson_delivery';
  } else if (sessionPhase === 'CHECKPOINT') {
    updates.current_interaction_mode = 'checkpoint';
  }

  return Object.keys(updates).length > 0 ? normalizeBehaviorVocabulary(updates) : null;
}

// ─────────────────────────────────────────────
// VOCABULARY NORMALIZATION
// Maps inferBehaviorUpdates' rich/descriptive values onto the discrete
// vocabularies the experience + motivation engines and the planner consume.
// Applied to the partial update before write-back. Descriptive nuance for the
// model is preserved in tone_guidance / next_best_response_style /
// momentum_bridge, which are intentionally left un-normalized.
// ─────────────────────────────────────────────
function normalizeBehaviorVocabulary(updates) {
  const sig = updates.last_emotional_signal;

  // learner_confidence → 'high' | 'medium' | 'low' | 'unknown'
  if (updates.learner_confidence != null) {
    const c = updates.learner_confidence;
    updates.learner_confidence =
      ['crisis', 'fragile', 'low'].includes(c)            ? 'low'
      : c === 'improving'                                 ? 'medium'
      : c === 'high_uncalibrated'                         ? 'high'
      : ['disengaged', 'draining'].includes(c)            ? 'medium'
      : ['high', 'medium', 'low', 'unknown'].includes(c)  ? c
      : 'unknown';
  }

  // learner_friction → 'high' | 'medium' | 'low' (severity of the fired pattern)
  if (updates.learner_friction != null) {
    updates.learner_friction =
      ['PANIC', 'SHAME', 'COGNITIVE_OVERLOAD'].includes(sig)                  ? 'high'
      : ['FATIGUE', 'AVOIDANCE', 'MOTIVATION_DIP', 'CONFUSION'].includes(sig) ? 'medium'
      : 'low';
  }

  // preferred_pace → 'slower' | 'normal' | 'faster'
  if (updates.preferred_pace != null) {
    const p = updates.preferred_pace;
    updates.preferred_pace =
      p === 'slow_concrete'                          ? 'slower'
      : (p === 'faster' || p === 'exam_accelerated') ? 'faster'
      : (['slower', 'normal', 'faster'].includes(p)) ? p
      : 'normal';
  }

  // current_support_need → 'guided' | 'independent' | 'none'
  if (updates.current_support_need != null) {
    updates.current_support_need =
      (sig === 'OVERCONFIDENCE' || updates.last_success) ? 'independent' : 'guided';
  }

  return updates;
}

// ─────────────────────────────────────────────
// CONVERSATION SUMMARIES
// Rolling summary that replaces lost context when old turns drop.
// Updated after meaningful turns. Injected before recent history.
// Fixes the cold-restart problem for returning learners.
// ─────────────────────────────────────────────

export async function getConversationSummary(learnerId, sessionId) {
  if (!learnerId) return null;
  const { data, error } = await supabase
    .from('conversation_summaries')
    .select('*')
    .eq('learner_id', learnerId)
    .eq('session_id', sessionId)
    .maybeSingle();
  if (error) { console.error('getConversationSummary:', error.message); return null; }
  return data || null;
}

export async function upsertConversationSummary(learnerId, sessionId, updates) {
  if (!learnerId || !sessionId) return;
  const { data: existing } = await supabase
    .from('conversation_summaries')
    .select('id')
    .eq('learner_id', learnerId)
    .eq('session_id', sessionId)
    .maybeSingle();

  const payload = {
    learner_id: learnerId,
    session_id: sessionId,
    ...updates,
    updated_at: new Date().toISOString(),
  };

  if (existing?.id) {
    const { error } = await supabase
      .from('conversation_summaries')
      .update(payload)
      .eq('id', existing.id);
    if (error) console.error('upsertConversationSummary update:', error.message);
  } else {
    const { error } = await supabase
      .from('conversation_summaries')
      .insert(payload);
    if (error) console.error('upsertConversationSummary insert:', error.message);
  }
}

// Get the most recent summary for a learner regardless of session
// Used for return-session bridge — what happened last time?
export async function getLastSessionSummary(learnerId) {
  if (!learnerId) return null;
  const { data, error } = await supabase
    .from('conversation_summaries')
    .select('*')
    .eq('learner_id', learnerId)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) { console.error('getLastSessionSummary:', error.message); return null; }
  return data || null;
}

// ─────────────────────────────────────────────────────────────────
// RETRIEVAL QUERIES
// Called by retrievalEngine.js before every AI call.
// Phase 1: structured queries from existing tables.
// Phase 2: augment with pgvector semantic search.
// ─────────────────────────────────────────────────────────────────

// Active knowledge gaps — concepts this learner has struggled with
export async function fetchWeakConcepts(learnerId, limit = 5, client = supabase) {
  if (!learnerId) return [];
  const { data, error } = await client
    .from('knowledge_gaps')
    .select('concept, error_type, severity, frequency')
    .eq('learner_id', learnerId)
    .eq('resolved', false)
    .order('frequency', { ascending: false })
    .limit(limit);
  if (error) { console.warn('fetchWeakConcepts:', error.message); return []; }
  return data || [];
}

// Recent incorrect quiz attempts — surfaces recurring error type patterns
export async function fetchErrorPatterns(learnerId, limit = 10, client = supabase) {
  if (!learnerId) return [];
  const { data, error } = await client
    .from('quiz_attempts')
    .select('error_type_detected')
    .eq('learner_id', learnerId)
    .eq('is_correct', false)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.warn('fetchErrorPatterns:', error.message); return []; }
  return data || [];
}

// Lesson completion state for the current track — roadmap position
export async function fetchRoadmapProgress(learnerId, trackId, client = supabase) {
  if (!learnerId || !trackId) return null;
  // Two queries in parallel:
  //   (1) lesson_progress for completed_count (status='completed' only — an
  //       in-progress current lesson doesn't count until its checkpoint passes).
  //   (2) true total lessons in the track (INNER-join lessons → modules by
  //       track_id; count: 'exact' + head: true returns just the count). This is
  //       the denominator — previously we used data.length which only counted
  //       *touched* lessons, so a learner on lesson 10 of 10 saw "10/10 · 100%"
  //       before passing it. Falls back to the touched-row count if this fails.
  const [progressRes, totalRes] = await Promise.all([
    client
      .from('lesson_progress')
      .select('status, mastery_level, lessons(title)')
      .eq('learner_id', learnerId)
      .eq('track_id', trackId),
    client
      .from('lessons')
      .select('id, modules!inner(track_id)', { count: 'exact', head: true })
      .eq('modules.track_id', trackId),
  ]);
  const { data, error } = progressRes;
  if (error) { console.warn('fetchRoadmapProgress:', error.message); return null; }
  if (totalRes?.error) console.warn('fetchRoadmapProgress total:', totalRes.error.message);

  const trueTotal = totalRes?.error ? null : (totalRes?.count ?? null);
  // Return null only when there's literally nothing to report (no touched rows
  // AND no track total). Otherwise show "X of Y" honestly, including 0/Y for
  // brand-new learners on a seeded track.
  if (!data?.length && !trueTotal) return null;

  const completed = (data || []).filter(r => r.status === 'completed');
  const weak      = (data || []).filter(r => r.mastery_level === 'struggling' || r.mastery_level === 'familiar');

  return {
    total_count:      trueTotal != null ? trueTotal : (data?.length || 0),
    completed_count:  completed.length,
    mastered_lessons: completed.map(r => r.lessons?.title).filter(Boolean),
    weak_lessons:     weak.map(r => r.lessons?.title).filter(Boolean),
  };
}

// Current-lesson position for the dual progress indicator on the Continue
// Learning surfaces:
//   overall     — 1-based ordinal of this lesson across the whole track
//                 (module order, then lesson order) → "Lesson 14 of 42"
//   moduleIndex — 1-based ordinal within its own module → "Module lesson 1 of 4"
// Track is resolved through modules because lessons.track_id is denormalized
// and frequently null (e.g. the A+ catalog). Returns null if the lesson can't
// be located so callers can fall back to the plain completed-count line.
export async function getLessonPosition(trackId, lessonId) {
  if (!trackId || !lessonId) return null;
  const { data: cur, error } = await supabase
    .from('lessons')
    .select('sequence_order, module_id, modules!inner(sequence_order, title, track_id)')
    .eq('id', lessonId)
    .maybeSingle();
  if (error) { console.warn('getLessonPosition:', error.message); return null; }
  if (!cur?.modules) return null;
  const modSeq = cur.modules.sequence_order;

  const [moduleTotalRes, earlierModsRes, totalRes] = await Promise.all([
    supabase.from('lessons').select('id', { count: 'exact', head: true }).eq('module_id', cur.module_id),
    supabase.from('modules').select('id').eq('track_id', trackId).lt('sequence_order', modSeq),
    supabase.from('lessons').select('id, modules!inner(track_id)', { count: 'exact', head: true }).eq('modules.track_id', trackId),
  ]);

  let lessonsBefore = 0;
  const earlierIds = (earlierModsRes.data || []).map(m => m.id);
  if (earlierIds.length) {
    const { count } = await supabase.from('lessons').select('id', { count: 'exact', head: true }).in('module_id', earlierIds);
    lessonsBefore = count || 0;
  }

  return {
    moduleTitle: cur.modules.title,
    moduleIndex: cur.sequence_order,
    moduleTotal: moduleTotalRes.count || 0,
    overall:     lessonsBefore + cur.sequence_order,
    total:       totalRes.count || 0,
  };
}

// Lesson objectives and module title for the current lesson
export async function fetchLessonContext(lessonId, client = supabase) {
  if (!lessonId) return null;
  const { data, error } = await client
    .from('lessons')
    .select('title, objectives, modules(title)')
    .eq('id', lessonId)
    .maybeSingle();
  if (error) { console.warn('fetchLessonContext:', error.message); return null; }
  if (!data) return null;
  return {
    title:        data.title,
    objectives:   data.objectives || [],
    module_title: data.modules?.title || null,
  };
}
// Cross-learner cohort memory (M[Emory] Ring 1). Reads the aggregate-only
// `cohort_concept_memory` table, which carries NO learner_id and its own
// public-read RLS policy — so the learner's own RLS-scoped client can read it.
// Returns rows only when a concept clears the k-anon gate (>= COHORT_K_ANON
// distinct learners) OR is an authored prior. Returns null on a miss or any
// error — a M[Emory] failure must never block the learning loop (same
// defensive contract as fetchWeakConcepts).
const COHORT_K_ANON = 8;
export async function fetchCohortMemory(lessonId, trackId, client = supabase) {
  if (!lessonId && !trackId) return null;
  let query = client
    .from('cohort_concept_memory')
    .select('concept_key, sample_size, source, common_misconception, top_wrong_answer, repair_that_worked, dominant_emotional_signal, relational_approach_that_worked');
  if (lessonId) {
    query = query.eq('lesson_id', lessonId);
  } else {
    query = query.eq('track_id', trackId);
  }
  // Either a cohort row that clears the k-anon gate, or an authored prior.
  query = query
    .or(`sample_size.gte.${COHORT_K_ANON},source.eq.authored`)
    .order('sample_size', { ascending: false })
    .limit(3);
  const { data, error } = await query;
  if (error) { console.warn('fetchCohortMemory:', error.message); return null; }
  // Belt-and-suspenders: a row must carry at least one usable directive.
  const usable = (data || []).filter(
    r => r.common_misconception || r.top_wrong_answer || r.repair_that_worked || r.relational_approach_that_worked
  );
  return usable.length ? usable : null;
}

export async function fetchConceptPrimitives(lessonId, trackId, client = supabase) {
  if (!lessonId && !trackId) return [];
  let query = client
    .from('concept_primitives')
    .select('concept_id, canonical_name, concept_type, compressed_model, core_operation, governing_rules, failure_patterns, transfer_checks, checkpoint_templates, abstraction_levels, hard_prerequisites, adjacent_concepts');
  if (lessonId) {
    query = query.eq('lesson_id', lessonId);
  } else if (trackId) {
    query = query.eq('track_id', trackId).is('lesson_id', null).limit(3);
  }
  const { data, error } = await query;
  if (error) { console.warn('fetchConceptPrimitives:', error.message); return []; }
  return data || [];
}
